import {
  GenericForm,
  GenericFormLogicalField,
  GenericFormValidationContext,
  genericBeforeValidate_removeDefaultKeyedError,
  GenericTextFieldValidationOptions,
  genericValidate_textField,
  ValidationEvent,
} from "../../../hooks";

export interface InitialStateMinimumRequirements_PasswordForm {
  password_form: {
    minimum_length: number;
    minimum_character_sets: number;
  };
}

export interface AppMinimumRequirementsForPasswordForm {
  initialState: InitialStateMinimumRequirements_PasswordForm;
}

interface PasswordGradingContext {
  passwordValue: string;
  initialState: InitialStateMinimumRequirements_PasswordForm;
}

interface PasswordGrade {
  isValid: boolean;

  passwordLength: number;
  characterSetsCount: number;

  hasMinimumCharacterLength: boolean;
  hasMinimumCharacterSets: boolean;
  hasDigits: boolean;
  hasLowerCase: boolean;
  hasSpecialCharacters: boolean;
  hasUpperCase: boolean;
}

export const gradePassword = (context: PasswordGradingContext) => {
  const thisPassword = context.passwordValue;
  const { password_form } = context.initialState;
  const { minimum_length, minimum_character_sets } = password_form;

  const hasMinimumCharacterLength = thisPassword.length >= minimum_length;

  let thisPasswordStrength = 0;
  let hasDigits = false;
  if (thisPassword.match(/\d+/)) {
    hasDigits = true;
    thisPasswordStrength++;
  }
  let hasLowerCase = false;
  if (thisPassword.match(/[a-z]+/)) {
    hasLowerCase = true;
    thisPasswordStrength++;
  }
  let hasUpperCase = false;
  if (thisPassword.match(/[A-Z]+/)) {
    hasUpperCase = true;
    thisPasswordStrength++;
  }
  let hasSpecialCharacters = false;
  if (thisPassword.match(/[^a-zA-Z0-9]+/)) {
    hasSpecialCharacters = true;
    thisPasswordStrength++;
  }

  const hasMinimumCharacterSets =
    thisPasswordStrength >= minimum_character_sets;

  let isValid = false;
  if (hasMinimumCharacterLength && hasMinimumCharacterSets) {
    isValid = true;
  }

  const grade: PasswordGrade = {
    isValid,

    passwordLength: thisPassword.length,
    characterSetsCount: thisPasswordStrength,

    hasMinimumCharacterLength,
    hasMinimumCharacterSets,
    hasDigits,
    hasLowerCase,
    hasSpecialCharacters,
    hasUpperCase,
  };

  return grade;
};

interface PasswordFormLogicProps {
  required?: boolean;
}

export const passwordFormLogic = <
  Form extends GenericForm,
  App extends AppMinimumRequirementsForPasswordForm
>({ required }: PasswordFormLogicProps = {}): GenericFormLogicalField<
  Form,
  App
> => ({
  validationEvents: [ValidationEvent.onBlur, ValidationEvent.onSubmit],
  cleanEvents: [
    ValidationEvent.onFocus,
    ValidationEvent.onChange,
    ValidationEvent.onBlur,
    ValidationEvent.onSubmit,
  ],
  beforeValidate: genericBeforeValidate_removeDefaultKeyedError,
  validate: (context) => {
    if (
      context.validationEvent === ValidationEvent.onFocus ||
      context.validationEvent === ValidationEvent.onChange
    ) {
      return;
    }

    genericValidate_textField({
      required,
      trim: true,
      customErrorValidation: (
        value: any,
        options: GenericTextFieldValidationOptions<Form, App>,
        context: GenericFormValidationContext<Form, App>
      ) => {
        const { initialState } = context.app;
        const { password_form } = initialState;
        const { minimum_length, minimum_character_sets } = password_form;

        const thisPassword = (value || "") as string;

        const grade = gradePassword({
          passwordValue: thisPassword,
          initialState,
        });

        if (!grade.hasMinimumCharacterLength) {
          return `Minimum length of this field must be equal or greater than ${minimum_length} symbols. Leading and trailing spaces will be ignored.`;
        }

        if (!grade.hasMinimumCharacterSets) {
          return `Minimum of different classes of characters in password is ${minimum_character_sets}. Classes of characters: Lower Case, Upper Case, Digits, Special Characters.`;
        }

        return null;
      },
    })(context);
  },
});

export const passwordForLoginFormLogic = <
  Form extends GenericForm,
  App extends AppMinimumRequirementsForPasswordForm
>({ required }: PasswordFormLogicProps = {}): GenericFormLogicalField<
  Form,
  App
> => ({
  validationEvents: [ValidationEvent.onBlur, ValidationEvent.onSubmit],
  cleanEvents: [
    ValidationEvent.onFocus,
    ValidationEvent.onChange,
    ValidationEvent.onBlur,
    ValidationEvent.onSubmit,
  ],
  beforeValidate: genericBeforeValidate_removeDefaultKeyedError,
  validate: genericValidate_textField({
    required,
  }),
});

interface PasswordConfirmFormLogicProps extends PasswordFormLogicProps {
  passwordFormKey: string;
}

export const passwordConfirmFormLogic = <
  Form extends GenericForm,
  App extends unknown
>({
  required,
  passwordFormKey,
}: PasswordConfirmFormLogicProps): GenericFormLogicalField<Form, App> => ({
  dependencies: [passwordFormKey],
  validationEvents: [ValidationEvent.onBlur, ValidationEvent.onSubmit],
  cleanEvents: [
    ValidationEvent.onFocus,
    ValidationEvent.onChange,
    ValidationEvent.onBlur,
    ValidationEvent.onSubmit,
  ],
  beforeValidate: genericBeforeValidate_removeDefaultKeyedError,
  validate: (context) => {
    const isThisAnEvent_whichTheOriginalPasswordFieldCaresAbout =
      context.validationEventHistory.some((anyValidationEventInTheStack) =>
        passwordFormLogic({ required }).validationEvents.some(
          (anyValidationEventThatThePasswordFieldCaresAboutForValidation) =>
            anyValidationEventInTheStack ===
            anyValidationEventThatThePasswordFieldCaresAboutForValidation
        )
      );
    if (!isThisAnEvent_whichTheOriginalPasswordFieldCaresAbout) {
      return;
    }

    let userHasEnteredFieldAtLeastOnce =
      context.form[`${context.name}_userHasEnteredFieldAtLeastOnce`];
    const isThisEventFromADependencyAtAll = context.validationEventHistory.some(
      (anyValidationEventInTheStack) =>
        anyValidationEventInTheStack === ValidationEvent.onDependencyEvent
    );
    const isThisADirectUserEvent = !isThisEventFromADependencyAtAll;
    if (isThisADirectUserEvent && !userHasEnteredFieldAtLeastOnce) {
      userHasEnteredFieldAtLeastOnce = true;
      const partialUpdate_withStateChange = {
        [`${context.name}_userHasEnteredFieldAtLeastOnce`]: true,
      } as Partial<Form>;
      context = context.mutate.withPartialForm(
        context,
        partialUpdate_withStateChange
      );
    }

    if (!userHasEnteredFieldAtLeastOnce) {
      return;
    }

    genericValidate_textField({
      required,
      trim: true,
      customErrorValidation: (
        value: any,
        options: GenericTextFieldValidationOptions<Form, App>,
        context: GenericFormValidationContext<Form, App>
      ) => {
        const thisPasswordConfirmation = (value || "") as string;
        const thisPassword = (context.form[passwordFormKey] || "") as string;

        if (thisPasswordConfirmation !== thisPassword) {
          return "Please enter the same value again.";
        }

        return null;
      },
    })(context);
  },
});
